import React, { useState, useEffect } from 'react';
import { Card, Form, DatePicker, TimePicker, Select, Button, Switch, Alert, Typography, Spin } from 'antd';
import { TeamOutlined, RocketTwoTone, WarningTwoTone } from '@ant-design/icons';
import dayjs from 'dayjs';
import { BaziChartGrid } from '../../components/BaziChartGrid';
import tenGodsProfiles from '../../data/ten_gods_profiles.json';

const { Title, Paragraph } = Typography;
const { Option } = Select;

interface SynergyProps {
  token: string;
  API_URL: string;
  isAdmin?: boolean;
}

export const Synergy: React.FC<SynergyProps> = ({ token, API_URL, isAdmin }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileData, setProfileData] = useState<any>(null);
  const [result, setResult] = useState<any>(null);
  const [chartA, setChartA] = useState<any>(null);
  const [chartB, setChartB] = useState<any>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [unknownTobA, setUnknownTobA] = useState(false);
  const [unknownTobB, setUnknownTobB] = useState(false);

  // Fetch user profile to pre-fill Pihak Pertama (read-only)
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API_URL}/api/v1/personal/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const result = await res.json();
          if (result.user) {
            const user = result.user;
            const isUnknownTob = !user.bazi_birth_time || user.bazi_birth_time === '00:00:00';
            setUnknownTobA(isUnknownTob);
            setProfileData(user);
            form.setFieldsValue({
              dob_a: user.bazi_birth_date ? dayjs(user.bazi_birth_date) : undefined,
              tob_a: user.bazi_birth_time && !isUnknownTob
                ? dayjs(user.bazi_birth_time, 'HH:mm:ss')
                : undefined,
              gender_a: user.gender || 'male',
              tz_a: user.bazi_birth_timezone || 'Asia/Jakarta',
            });
          }
        }
      } catch (err) {
        console.error('Gagal memuat profil:', err);
      } finally {
        setProfileLoading(false);
      }
    };
    fetchProfile();
  }, [token, API_URL, form]);

  const handleCompute = async (values: any) => {
    setLoading(true);
    setErrorMsg('');
    try {
      // Pihak Pertama — from saved profile (read-only), or form values (admin)
      let dobAStr: string, tobAStr: string, genderA: string, tzA: string;
      if (isAdmin && values.dob_a) {
        dobAStr = values.dob_a.format('YYYY-MM-DD');
        tobAStr = unknownTobA || !values.tob_a ? '12:00' : values.tob_a.format('HH:mm');
        genderA = values.gender_a || 'male';
        tzA = values.tz_a || 'Asia/Jakarta';
      } else {
        const rawDate = profileData?.bazi_birth_date;
        dobAStr = rawDate ? dayjs(rawDate).format('YYYY-MM-DD') : '1990-05-15';
        const rawTime = profileData?.bazi_birth_time;
        tobAStr = unknownTobA || !rawTime ? '12:00' : (typeof rawTime === 'string' ? rawTime.substring(0, 5) : dayjs(rawTime).format('HH:mm'));
        genderA = profileData?.gender || 'male';
        tzA = profileData?.bazi_birth_timezone || 'Asia/Jakarta';
      }

      const dobBStr = values.dob_b ? (typeof values.dob_b.format === 'function' ? values.dob_b.format('YYYY-MM-DD') : String(values.dob_b)) : '1988-08-20';
      const tobBStr = unknownTobB || !values.tob_b ? '12:00' : (typeof values.tob_b.format === 'function' ? values.tob_b.format('HH:mm') : String(values.tob_b));
      const genderB = values.gender_b || 'female';
      const tzB = values.tz_b || 'Asia/Jakarta';

      // 1. Calculate Chart A
      const resA = await fetch(`${API_URL}/api/v1/personal/charts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          date_of_birth: dobAStr,
          time_of_birth: tobAStr,
          unknown_tob: unknownTobA,
          timezone: tzA,
          gender: genderA,
          person_name: 'Orang A'
        })
      });

      // 2. Calculate Chart B
      const resB = await fetch(`${API_URL}/api/v1/personal/charts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          date_of_birth: dobBStr,
          time_of_birth: tobBStr,
          unknown_tob: unknownTobB,
          timezone: tzB,
          gender: genderB,
          person_name: 'Orang B'
        })
      });

      if (!resA.ok) {
        const errA = await resA.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(`Gagal memproses bagan kamu: ${errA.error || resA.status}`);
      }
      if (!resB.ok) {
        const errB = await resB.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(`Gagal memproses bagan pembanding: ${errB.error || resB.status}`);
      }

      const dataA = await resA.json();
      const dataB = await resB.json();

      // 3. Compute Synergy Compatibility
      const resSyn = await fetch(`${API_URL}/api/v1/personal/charts/synergy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          chart_id_a: dataA.data.chart_id,
          chart_id_b: dataB.data.chart_id
        })
      });

      if (!resSyn.ok) throw new Error('Perhitungan sinergi gagal.');

      const dataSyn = await resSyn.json();
      setResult(dataSyn.data);
      setChartA({ ...dataA.data, unknown_tob: unknownTobA });
      setChartB({ ...dataB.data, unknown_tob: unknownTobB });
      setTimeout(() => window.scrollTo({ top: 450, behavior: 'smooth' }), 100);
    } catch (err: any) {
      setErrorMsg(err.message || 'Gagal menghitung kecocokan.');
    } finally {
      setLoading(false);
    }
  };

  if (profileLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column', gap: '16px' }}>
        <Spin size="large" />
        <span style={{ fontWeight: 800 }}>Memuat profil kamu...</span>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '12px' }}>
      <Card style={{ 
        border: '3px solid #1a202c', 
        borderRadius: '16px', 
        boxShadow: '4px 4px 0 #1a202c',
        marginBottom: '24px'
      }}>
        <div style={{ borderBottom: '2px solid #1a202c', paddingBottom: '12px', marginBottom: '24px' }}>
          <Title level={3} style={{ margin: 0, fontWeight: 900 }}><TeamOutlined style={{ marginRight: 8 }} /> Cek Kecocokan Hubungan</Title>
          <Paragraph style={{ margin: '4px 0 0 0', color: '#495057', fontWeight: 650 }}>
            Uji tingkat kecocokan BaZi kamu dengan pasangan, gebetan, atau rekan bisnis.
          </Paragraph>
        </div>

        {errorMsg && (
          <Alert message={errorMsg} type="error" showIcon style={{ marginBottom: '20px', border: '2px solid #e03131', borderRadius: '8px' }} />
        )}

        <Form 
          form={form}
          layout="vertical" 
          onFinish={handleCompute}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            {/* Pihak Pertama — read-only untuk user biasa, editable untuk admin */}
            <Card title={
              <span style={{ fontWeight: 800 }}>
                Pihak Pertama{' '}
                {isAdmin
                  ? <span style={{ fontWeight: 600, fontSize: '0.75rem', color: '#e03131' }}>— ubah untuk ujicoba</span>
                  : <span style={{ fontWeight: 600, fontSize: '0.75rem', color: '#868e96' }}>— dari Profil Tersimpan</span>}
              </span>
            } style={{ border: '2px solid #1a202c', borderRadius: '8px' }}>
              <Form.Item label={<span style={{ fontWeight: 700 }}>Tanggal Lahir</span>} name="dob_a" rules={[{ required: isAdmin, message: 'Pilih tanggal lahir' }]}>
                <DatePicker disabled={!isAdmin} inputReadOnly needConfirm style={{ width: '100%', border: '2px solid #1a202c', borderRadius: '8px' }} />
              </Form.Item>

              <Form.Item label={<span style={{ fontWeight: 700 }}>Jam Lahir</span>} name="tob_a">
                <TimePicker disabled={!isAdmin || unknownTobA} inputReadOnly format="HH:mm" style={{ width: '100%', border: '2px solid #1a202c', borderRadius: '8px' }} />
              </Form.Item>
              {isAdmin && (
                <Form.Item label={<span style={{ fontWeight: 700, fontSize: '0.8rem' }}>Jam Lahir Tidak Diketahui?</span>}>
                  <Switch checked={unknownTobA} onChange={setUnknownTobA} style={{ background: unknownTobA ? '#2b8a3e' : '#ced4da' }} />
                </Form.Item>
              )}

              <Form.Item label={<span style={{ fontWeight: 700 }}>Jenis Kelamin</span>} name="gender_a" rules={isAdmin ? [{ required: true }] : []}>
                <Select disabled={!isAdmin} style={{ width: '100%', borderRadius: '8px' }}>
                  <Option value="male">Laki-Laki</Option>
                  <Option value="female">Perempuan</Option>
                </Select>
              </Form.Item>

              <Form.Item label={<span style={{ fontWeight: 700 }}>Zona Waktu</span>} name="tz_a">
                <Select disabled={!isAdmin} style={{ width: '100%', borderRadius: '8px' }}>
                  <Option value="Asia/Jakarta">WIB (Jakarta)</Option>
                  <Option value="Asia/Makassar">WITA (Bali/Makassar)</Option>
                  <Option value="Asia/Jayapura">WIT (Papua)</Option>
                  <Option value="Asia/Singapore">Singapore (GMT+8)</Option>
                </Select>
              </Form.Item>
            </Card>

            {/* Pihak Kedua */}
            <Card title={<span style={{ fontWeight: 800 }}>Pihak Kedua (Pembanding)</span>} style={{ border: '2px solid #1a202c', borderRadius: '8px' }}>
              <Form.Item label={<span style={{ fontWeight: 700 }}>Tanggal Lahir</span>} name="dob_b" rules={[{ required: true }]}>
                <DatePicker inputReadOnly needConfirm style={{ width: '100%', border: '2px solid #1a202c', borderRadius: '8px' }} />
              </Form.Item>

              <Form.Item label={<span style={{ fontWeight: 700 }}>Jam Lahir</span>} name="tob_b" rules={[{ required: !unknownTobB }]}>
                <TimePicker inputReadOnly format="HH:mm" disabled={unknownTobB} style={{ width: '100%', border: '2px solid #1a202c', borderRadius: '8px' }} />
              </Form.Item>

              <Form.Item label={<span style={{ fontWeight: 700, fontSize: '0.8rem' }}>Jam Lahir Tidak Diketahui?</span>}>
                <Switch checked={unknownTobB} onChange={setUnknownTobB} style={{ background: unknownTobB ? '#2b8a3e' : '#ced4da' }} />
              </Form.Item>

              <Form.Item label={<span style={{ fontWeight: 700 }}>Jenis Kelamin</span>} name="gender_b" rules={[{ required: true }]}>
                <Select style={{ width: '100%', borderRadius: '8px' }}>
                  <Option value="male">Laki-Laki</Option>
                  <Option value="female">Perempuan</Option>
                </Select>
              </Form.Item>

              <Form.Item label={<span style={{ fontWeight: 700 }}>Zona Waktu</span>} name="tz_b" initialValue="Asia/Jakarta">
                <Select style={{ width: '100%', borderRadius: '8px' }}>
                  <Option value="Asia/Jakarta">WIB (Jakarta)</Option>
                  <Option value="Asia/Makassar">WITA (Bali/Makassar)</Option>
                  <Option value="Asia/Jayapura">WIT (Papua)</Option>
                  <Option value="Asia/Singapore">Singapore (GMT+8)</Option>
                </Select>
              </Form.Item>
            </Card>
          </div>

          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            icon={<TeamOutlined />}
            style={{
              height: '46px',
              background: '#2b8a3e',
              borderColor: '#1a202c',
              color: '#ffffff',
              fontWeight: 900,
              fontSize: '1rem',
              boxShadow: '3px 3px 0 #1a202c',
              borderRadius: '10px',
              marginTop: '24px'
            }}
          >
            Cek Kecocokan Hubungan
          </Button>
        </Form>
      </Card>

      {/* Results Display */}
      {result && (() => {
        const lbl = (result.bazi_synergy_label || '').toUpperCase();
        let bg = '#FFFF65'; let color = '#555000'; // AVERAGE
        if (lbl === 'VERY GOOD') { bg = '#e8f4fd'; color = '#0071C1'; } // VERY GOOD (Biru)
        if (lbl === 'GOOD') { bg = '#B5E6A2'; color = '#2d6b1e'; } // GOOD (Hijau)
        if (lbl === 'CHALLENGE') { bg = '#FF66FF'; color = '#990099'; } // CHALLENGE
        if (lbl === 'BAD') { bg = '#ffe6ee'; color = '#FF0068'; } // BAD

        // — Dominant star helper —
        const relMap: Record<string, string> = {
          '比肩': 'F', '劫财': 'RW', '食神': 'EG', '伤官': 'HO', '偏财': 'IW',
          '正财': 'DW', '七杀': '7K', '正官': 'DO', '偏印': 'IR', '正印': 'DR'
        };
        const getDominantStar = (chart: any) => {
          const tr = chart?.ten_relations;
          if (!tr) return null;
          const code = relMap[tr.month_stem] || relMap[tr.year_stem] || relMap[tr.hour_stem] || 'DW';
          const profiles = tenGodsProfiles as Record<string, any>;
          return { code, ...(profiles[code] || { name: code, title: '' }) };
        };
        const starA = chartA ? getDominantStar(chartA) : null;
        const starB = chartB ? getDominantStar(chartB) : null;
        return (
          <Card style={{ 
            border: '3px solid #1a202c', 
            borderRadius: '16px', 
            boxShadow: '4px 4px 0 #1a202c',
            backgroundColor: bg
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <strong>Skor Kecocokan (Synergy Score):</strong>
                <div style={{ fontSize: '2rem', fontWeight: 950, color: color, marginTop: '6px' }}>
                  {Math.round(Number(result.bazi_synergy_score || 0))}% <span style={{ fontSize: '1.2rem', fontWeight: 800 }}>({result.bazi_synergy_label})</span>
                </div>
              </div>

            {/* Dominant Stars for both parties */}
            {starA && starB && (
              <div style={{
                border: '2px solid #1a202c', borderRadius: '12px', padding: '16px',
                backgroundColor: '#ffffff', boxShadow: '3px 3px 0 #1a202c'
              }}>
                <strong style={{ fontSize: '0.9rem', display: 'block', borderBottom: '1px solid #1a202c', paddingBottom: '6px', marginBottom: '12px' }}>
                  <TeamOutlined style={{ marginRight: 8 }} /> Pilar Dominan Masing-Masing Pihak:
                </strong>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{
                    flex: 1, minWidth: '180px', border: '2px solid #1098ad', borderRadius: '10px',
                    padding: '12px', background: '#e7f5ff'
                  }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#868e96', textTransform: 'uppercase', marginBottom: '2px' }}>Pihak Pertama (Kamu)</div>
                    <div style={{ fontWeight: 900, fontSize: '1.4rem', color: '#1098ad' }}>{starA.code}</div>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a202c' }}>{starA.name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#495057', fontWeight: 600, marginTop: '4px' }}>{starA.title}</div>
                  </div>
                  <div style={{
                    flex: 1, minWidth: '180px', borderColor: '#d9480f', borderWidth: '2px', borderStyle: 'solid', borderRadius: '10px',
                    padding: '12px', background: '#fff5f5'
                  }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#868e96', textTransform: 'uppercase', marginBottom: '2px' }}>Pihak Kedua (Pembanding)</div>
                    <div style={{ fontWeight: 900, fontSize: '1.4rem', color: '#d9480f' }}>{starB.code}</div>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a202c' }}>{starB.name}</div>
                    <div style={{ fontSize: '0.75rem', color: '#495057', fontWeight: 600, marginTop: '4px' }}>{starB.title}</div>
                  </div>
                </div>
              </div>
            )}

            {result.ten_gods_interaction && (
              <div style={{ 
                border: '2px solid #1a202c', 
                borderRadius: '12px', 
                padding: '16px', 
                backgroundColor: '#fff9db',
                boxShadow: '3px 3px 0 #1a202c'
              }}>
                <strong style={{ fontSize: '0.9rem', display: 'block', borderBottom: '1px solid #1a202c', paddingBottom: '6px', marginBottom: '12px' }}>
                  <TeamOutlined style={{ marginRight: 8 }} /> Analisis Interaksi 10 Bintang (Ten Gods):
                </strong>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ fontWeight: 800, fontSize: '0.85rem', color: '#d9480f', marginBottom: '8px' }}>
                    Dinamika: {result.ten_gods_interaction.dynamics}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#2b8a3e' }}><><RocketTwoTone twoToneColor="#52c41a" style={{ marginRight: 8 }} /> Potensi Kerjasama:</></div>
                    <div style={{ fontSize: '0.8rem', color: '#2d3748', marginTop: '2px', fontWeight: 600 }}>{result.ten_gods_interaction.potential}</div>
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#c92a2a' }}><><WarningTwoTone twoToneColor="#f5222d" style={{ marginRight: 8 }} /> Potensi Konflik / Tantangan:</></div>
                    <div style={{ fontSize: '0.8rem', color: '#2d3748', marginTop: '2px', fontWeight: 600 }}>{result.ten_gods_interaction.conflict}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Reference Charts */}
            {chartA && chartB && (
              <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <Card title="Referensi Bagan BaZi: Pihak Pertama (Kamu)" style={{ border: '2px solid #1a202c', borderRadius: '12px' }}>
                  <div style={{ overflowX: 'auto', display: 'flex', justifyContent: 'center' }}>
                    <BaziChartGrid chart={chartA} unknownTob={Boolean(chartA.unknown_tob)} />
                  </div>
                </Card>
                <Card title="Referensi Bagan BaZi: Pihak Kedua (Pembanding)" style={{ border: '2px solid #1a202c', borderRadius: '12px' }}>
                  <div style={{ overflowX: 'auto', display: 'flex', justifyContent: 'center' }}>
                    <BaziChartGrid chart={chartB} unknownTob={Boolean(chartB.unknown_tob)} />
                  </div>
                </Card>
              </div>
            )}
          </div>
        </Card>
      );
    })()}
    </div>
  );
};
export default Synergy;
